'use client'

import { useState } from 'react'
import { Button, Tag, InputNumber } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import { formatGbp } from 'src/utils/formatGbp'

const RecipeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 4px;
  background: ${props => (props.isLocked ? '#f0f8ff' : '#fafafa')};
  border: 1px solid ${props => (props.isLocked ? '#b5d922' : '#f0f0f0')};
`

const RecipeInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const RecipeName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
`

const RecipeDetails = styled.div`
  font-size: 12px;
  color: #666;
`

const AllergenTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`

const ExpandedContent = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
`

const IngredientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const IngredientItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 12px;
`

const RecipeIngredientRow = ({
  recipeIngredient,
  onUpdate,
  onRemove,
  onToggleLock,
  availableRecipes = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [editingPortions, setEditingPortions] = useState(false)
  const [tempPortions, setTempPortions] = useState(recipeIngredient.portions)

  const handlePortionUpdate = () => {
    if (tempPortions > 0) {
      const updatedIngredient = {
        ...recipeIngredient,
        portions: tempPortions,
        totalCost: recipeIngredient.costPerPortion * tempPortions,
      }
      onUpdate(updatedIngredient)
    }
    setEditingPortions(false)
  }

  const handleToggleLock = () => {
    const updatedIngredient = {
      ...recipeIngredient,
      isLocked: !recipeIngredient.isLocked,
    }
    onToggleLock(updatedIngredient)
  }

  // Get current recipe data (for locked recipes, this might be updated)
  const currentRecipeData = recipeIngredient.isLocked
    ? availableRecipes.find(r => r._id === recipeIngredient.recipeId) ||
      recipeIngredient.baseRecipe
    : recipeIngredient.baseRecipe

  return (
    <div>
      <RecipeRow isLocked={recipeIngredient.isLocked}>
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </div>

        <RecipeInfo>
          <RecipeName>
            <span>
              {recipeIngredient?.recipeName ?? currentRecipeData?.title}
            </span>
            <Tag color='blue'>📖 Recipe</Tag>
            {recipeIngredient.isLocked ? (
              <LockOutlined style={{ color: '#52c41a' }} />
            ) : (
              <UnlockOutlined style={{ color: '#faad14' }} />
            )}
          </RecipeName>

          <RecipeDetails>
            {editingPortions ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InputNumber
                  size='small'
                  min={0.1}
                  step={0.1}
                  value={tempPortions}
                  onChange={setTempPortions}
                  style={{ width: 80 }}
                />
                <Button
                  size='small'
                  type='primary'
                  onClick={handlePortionUpdate}
                >
                  Save
                </Button>
                <Button
                  size='small'
                  onClick={() => {
                    setEditingPortions(false)
                    setTempPortions(recipeIngredient.portions)
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <span
                onClick={() => setEditingPortions(true)}
                style={{ cursor: 'pointer' }}
              >
                {recipeIngredient.portions} portion
                {recipeIngredient.portions !== 1 ? 's' : ''} |{' '}
                {formatGbp(recipeIngredient.totalCost)}
                <EditOutlined style={{ marginLeft: 4, fontSize: 10 }} />
              </span>
            )}
          </RecipeDetails>

          {(currentRecipeData?.allergens?.length ||
            recipeIngredient?.allergens?.length) > 0 && (
            <AllergenTags>
              {(
                currentRecipeData?.allergens ||
                recipeIngredient?.allergens ||
                []
              ).map(allergen => (
                <Tag key={allergen} size='small' color='orange'>
                  {allergen}
                </Tag>
              ))}
            </AllergenTags>
          )}
        </RecipeInfo>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size='small'
            onClick={handleToggleLock}
            title={recipeIngredient.isLocked ? 'Unlock recipe' : 'Lock recipe'}
          >
            {recipeIngredient.isLocked ? (
              <UnlockOutlined style={{ color: '#faad14' }} />
            ) : (
              <LockOutlined style={{ color: '#52c41a' }} />
            )}
          </Button>
          <Button
            size='small'
            onClick={() => onRemove(recipeIngredient.id)}
            danger
          >
            <DeleteOutlined />
          </Button>
        </div>
      </RecipeRow>

      {isExpanded && (
        <ExpandedContent>
          {recipeIngredient.isLocked ? (
            <div>
              <div
                style={{
                  padding: 8,
                  backgroundColor: '#e6f7ff',
                  borderRadius: 4,
                  marginBottom: 8,
                  fontSize: 12,
                  color: '#1890ff',
                }}
              >
                <LockOutlined style={{ marginRight: 4 }} />
                This recipe is linked to the base recipe. Any changes to the
                base recipe will automatically update here.
                {recipeIngredient.wasAutoUpdated ? (
                  <span style={{ marginLeft: 6, color: '#722ed1' }}>
                    (auto-updated)
                  </span>
                ) : null}
              </div>

              {currentRecipeData && (
                <div>
                  <strong>Base Recipe Info:</strong>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    Total Portions: {currentRecipeData.portions || 1}
                    <br />
                    Cost Per Portion:{' '}
                    {formatGbp(currentRecipeData.costPerPortion)}
                    <br />
                    Last Updated:{' '}
                    {currentRecipeData.updatedAt
                      ? new Date(
                          currentRecipeData.updatedAt,
                        ).toLocaleDateString()
                      : 'Unknown'}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <strong>Ingredient Breakdown:</strong>
                {currentRecipeData?.ingredients &&
                currentRecipeData.ingredients.length > 0 ? (
                  <IngredientList>
                    {currentRecipeData.ingredients.map((ingredient, index) => (
                      <IngredientItem key={index}>
                        <span>
                          {ingredient.product?.title ||
                            ingredient.title ||
                            'Unknown Ingredient'}
                        </span>
                        <span>
                          {ingredient.size} {ingredient.UoM} - £
                          {(ingredient.product.price / 100 || 0).toFixed(2)}
                        </span>
                      </IngredientItem>
                    ))}
                  </IngredientList>
                ) : (
                  <div
                    style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}
                  >
                    No ingredient details available
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <strong>Ingredient Breakdown:</strong>
              {currentRecipeData?.ingredients &&
              currentRecipeData.ingredients.length > 0 ? (
                <IngredientList>
                  {currentRecipeData.ingredients.map((ingredient, index) => (
                    <IngredientItem key={index}>
                      <span>
                        {ingredient.product?.title ||
                          ingredient.title ||
                          'Unknown Ingredient'}
                      </span>
                      <span>
                        {ingredient.size} {ingredient.UoM} - £
                        {(ingredient.product.price / 100 || 0).toFixed(2)}
                      </span>
                    </IngredientItem>
                  ))}
                </IngredientList>
              ) : (
                <div
                  style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}
                >
                  No ingredient details available
                </div>
              )}
            </div>
          )}
        </ExpandedContent>
      )}
    </div>
  )
}

export default RecipeIngredientRow
